import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "nw_theme";
const ThemeCtx = createContext(null);

/** Resolves the effective theme ('light' or 'dark') from a preference ('light' | 'dark' | 'auto'). */
function resolveEffective(pref) {
  if (pref === "light" || pref === "dark") return pref;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return "dark";
}

/** Applies the effective theme to <html> and updates the browser's theme-color meta. */
function applyTheme(effective) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", effective);
  // Also toggle Tailwind's `dark` class since tailwind.config uses darkMode: ["class"]
  document.documentElement.classList.toggle("dark", effective === "dark");
  const themeColor = effective === "light" ? "#F5F3EC" : "#050505";
  let meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", themeColor);
  } else {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    meta.setAttribute("content", themeColor);
    document.head.appendChild(meta);
  }
}

export function ThemeProvider({ children }) {
  // 'light' | 'dark' | 'auto' — 'auto' follows OS system preference
  // Default is 'dark' so first-time visitors see the intended NITEWIRE aesthetic.
  const [preference, setPreference] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(STORAGE_KEY) || "dark";
  });
  const [effective, setEffective] = useState(() => resolveEffective(
    typeof window !== "undefined" ? (localStorage.getItem(STORAGE_KEY) || "dark") : "dark"
  ));

  // Recompute effective + apply whenever preference changes
  useEffect(() => {
    const eff = resolveEffective(preference);
    setEffective(eff);
    applyTheme(eff);
    if (preference === "dark") {
      // 'dark' is now the default — no need to persist it explicitly
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, preference);
    }
  }, [preference]);

  // Follow OS preference changes when in 'auto' mode
  useEffect(() => {
    if (preference !== "auto" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      const eff = mq.matches ? "light" : "dark";
      setEffective(eff);
      applyTheme(eff);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [preference]);

  const setTheme = useCallback((p) => {
    setPreference(p);
  }, []);

  const value = useMemo(() => ({ preference, effective, setTheme }), [preference, effective, setTheme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
